import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Check, ChevronDown } from "lucide-react";
import { useDispatch } from "react-redux";
import ApiCollections from "../../configs/services/apiCollections";
import { fetchEbayStatusRequest } from "../../redux/ebay/ebay.actions";
import { toastify } from "../../configs/utils";

// Styled Components
const PoliciesContainer = styled.div`
  padding: 2rem;
  width: 100%;
  background: #ededed;
  min-height: 100%;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

const PoliciesActionsBar = styled.div`
  margin: 0 0 2rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const RefreshButton = styled.button`
  padding: 0.375rem 0.875rem;
  height: 2.125rem;
  background: transparent;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: #9ca3af;
    background: #ffffff;
  }

  &:active:not(:disabled) {
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: #6b7280;
  font-size: 1.1rem;
`;

const ErrorState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: #ef4444;
  font-size: 1.1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
`;

const ErrorLink = styled.a`
  color: #2563eb;
  text-decoration: underline;
  word-break: break-word;
`;

const BusinessPolicyOptInMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: center;
`;

const PolicySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const PolicySectionTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const PolicyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PolicyItem = styled.div`
  padding: 1rem;
  background: transparent;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 12px;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

const PolicyContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PolicyHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  cursor: pointer;
  user-select: none;
`;

const PolicyHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PolicyExpandButton = styled.button`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  min-width: 1.75rem;
  min-height: 1.75rem;
  padding: 0;
  margin: 0;
  flex: 0 0 auto;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    transform 0.15s ease;
  transform: rotate(${(props) => (props.$expanded ? "180deg" : "0deg")});

  svg {
    display: block;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    stroke: currentColor;
  }

  &:hover {
    background: #e5e7eb;
    border-color: #d1d5db;
  }
`;

const PolicyName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
`;

const PolicyId = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-family: monospace;
`;

const PolicyDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #4b5563;
`;

const PolicyDetailRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PolicyDetailLabel = styled.span`
  font-weight: 600;
  color: #374151;
`;

const PolicyDetailValue = styled.span`
  color: #6b7280;
`;

const LoadingText = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

const CheckboxWrapper = styled.label`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 0.25rem;
  cursor: pointer;
`;

const HiddenCheckboxInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
`;

const CheckboxBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid ${(props) => (props.$checked ? "#3b82f6" : "#d1d5db")};
  background: ${(props) => (props.$checked ? "#3b82f6" : "transparent")};
  transition: background-color 0.15s ease, border-color 0.15s ease;
  pointer-events: none;

  svg {
    width: 10px;
    height: 10px;
    color: white;
    opacity: ${(props) => (props.$checked ? 1 : 0)};
    transform: scale(${(props) => (props.$checked ? 1 : 0.5)});
    transition: opacity 0.15s ease, transform 0.15s ease;
  }

  ${HiddenCheckboxInput}:hover ~ & {
    border-color: ${(props) => (props.$checked ? "#2563eb" : "#9ca3af")};
  }

  ${HiddenCheckboxInput}:focus-visible ~ & {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
  }
`;

const PolicyCheckbox = ({ checked, onChange }) => (
  <CheckboxWrapper>
    <HiddenCheckboxInput
      type="checkbox"
      checked={checked}
      onChange={onChange}
    />
    <CheckboxBox $checked={checked}>
      <Check strokeWidth={3} />
    </CheckboxBox>
  </CheckboxWrapper>
);

const UpdateButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 1.5rem 0;
  border-top: 1px solid #e5e7eb;
  margin-top: 2rem;
`;

const UpdatePolicyButton = styled.button`
  background: #3b82f6;
  color: white;
  border: 1px solid #3b82f6;
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(59, 130, 246, 0.2);

  &:hover:not(:disabled) {
    background: #2563eb;
    border-color: #2563eb;
  }

  &:active:not(:disabled) {
    background: #1d4ed8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CreatePolicyButton = styled.button`
  background: #3b82f6;
  color: white;
  border: 1px solid #3b82f6;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(59, 130, 246, 0.2);

  &:hover {
    background: #2563eb;
    border-color: #2563eb;
  }

  &:active {
    background: #1d4ed8;
  }
`;

const PolicySectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
`;

const ForbiddenState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  background: transparent;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 12px;
  text-align: center;
`;

const ForbiddenTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
`;

const ForbiddenMessage = styled.p`
  font-size: 1rem;
  color: #6b7280;
  margin: 0 0 2rem 0;
  max-width: 500px;
`;

const ConnectToEbayButton = styled.button`
  background: #3b82f6;
  color: white;
  border: 1px solid #3b82f6;
  padding: 0.625rem 1.75rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(59, 130, 246, 0.2);

  &:hover:not(:disabled) {
    background: #2563eb;
    border-color: #2563eb;
  }

  &:active:not(:disabled) {
    background: #1d4ed8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const VatCard = styled.div`
  padding: 1rem;
  background: transparent;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 12px;
`;

const VatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const VatInputWrapper = styled.div`
  display: flex;
  align-items: center;
  max-width: 320px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: transparent;
  box-sizing: border-box;
  transition: border-color 0.15s ease;

  &:focus-within {
    border-color: #9ca3af;
  }
`;

const VatPercentSuffix = styled.span`
  padding: 0.75rem 0.75rem 0.75rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
`;

const VatInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.75rem;
  border: none;
  font-size: 0.875rem;
  font-family: inherit;
  color: #111827;
  box-sizing: border-box;
  background: transparent;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
  }
`;

const VAT_INFO_STORAGE_KEY = "stickerchecker_policies_vat_info";

const sanitizeVatInput = (value) => {
  const digits = String(value).replace(/\D/g, "");
  if (!digits) return "";
  const num = parseInt(digits, 10);
  if (num === 0) return "";
  if (num > 100) return "100";
  return String(num);
};

const parseVatPercentage = (value) => {
  const sanitized = sanitizeVatInput(value);
  if (!sanitized) return null;
  const num = parseInt(sanitized, 10);
  if (num < 1 || num > 100) return null;
  return num;
};

function Policies() {
  const dispatch = useDispatch();
  const [fulfillmentPolicies, setFulfillmentPolicies] = useState([]);
  const [paymentPolicies, setPaymentPolicies] = useState([]);
  const [returnPolicies, setReturnPolicies] = useState([]);
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(true);
  const [error, setError] = useState("");
  const [is403Error, setIs403Error] = useState(false);
  const [selectedFulfillmentPolicy, setSelectedFulfillmentPolicy] = useState("");
  const [selectedPaymentPolicy, setSelectedPaymentPolicy] = useState("");
  const [selectedReturnPolicy, setSelectedReturnPolicy] = useState("");
  const [isUpdatingPolicies, setIsUpdatingPolicies] = useState(false);
  const [isConnectingToEbay, setIsConnectingToEbay] = useState(false);
  const [vatInfo, setVatInfo] = useState("");
  const [expandedPolicies, setExpandedPolicies] = useState({});

  const togglePolicyExpanded = (policyKey) => {
    setExpandedPolicies((prev) => ({ ...prev, [policyKey]: !prev[policyKey] }));
  };

  const getBusinessPolicyOptInErrorMessage = (err) => {
    const errorData = err?.response?.data ?? err?.data;
    const longMessages = [];

    const collectMessagesFromItems = (items) => {
      items.forEach((item) => {
        const itemErrors = item?.data?.errors;
        if (Array.isArray(itemErrors)) {
          itemErrors.forEach((e) => {
            if (typeof e?.longMessage === "string") longMessages.push(e.longMessage);
            if (typeof e?.message === "string") longMessages.push(e.message);
          });
        }
      });
    };

    if (Array.isArray(errorData)) {
      collectMessagesFromItems(errorData);
    } else if (Array.isArray(errorData?.data)) {
      collectMessagesFromItems(errorData.data);
    }

    const hasBusinessPolicyEligibilityIssue = longMessages.some((m) => {
      const msg = String(m || "").toLowerCase();
      return msg.includes("not eligible") && msg.includes("business policy");
    });

    if (hasBusinessPolicyEligibilityIssue) {
      return `Your eBay account is not eligible for Business Policies. You can opt in for business policies by visiting https://www.ebay.com/bp/policyoptin`;
    }

    return null;
  };

  const getBusinessPolicyOptInErrorNode = () => (
    <BusinessPolicyOptInMessage>
      <div>
        Your eBay account is not eligible for <b>Business Policies</b>.
      </div>
      <div>
        You can opt in for business policies by visiting{" "}
        <ErrorLink
          href="https://www.ebay.com/bp/policyoptin"
          target="_blank"
          rel="noreferrer"
        >
          https://www.ebay.com/bp/policyoptin
        </ErrorLink>
      </div>
    </BusinessPolicyOptInMessage>
  );

  const fetchPolicies = async () => {
    setIsLoadingPolicies(true);
    setError("");
    setIs403Error(false);
    try {
      const response = await ApiCollections.getAllPolicies({ marketplace_id: "EBAY_DE" });
      
      // Handle both cases: array directly or wrapped in data property
      const policiesArray = Array.isArray(response?.data) 
        ? response.data 
        : (Array.isArray(response) ? response : []);

      // Initialize policy arrays
      let fulfillmentPolicies = [];
      let paymentPolicies = [];
      let returnPolicies = [];

      // Map through the array and extract policies based on type
      policiesArray.forEach((policyItem) => {
        // Check if status code is success (200)
        if (policyItem.status_code === 200 && policyItem.data) {
          if (policyItem.type === "fulfillment" && policyItem.data.fulfillmentPolicies) {
            fulfillmentPolicies = Array.isArray(policyItem.data.fulfillmentPolicies) 
              ? policyItem.data.fulfillmentPolicies 
              : [];
          } else if (policyItem.type === "payment" && policyItem.data.paymentPolicies) {
            paymentPolicies = Array.isArray(policyItem.data.paymentPolicies) 
              ? policyItem.data.paymentPolicies 
              : [];
          } else if (policyItem.type === "return" && policyItem.data.returnPolicies) {
            returnPolicies = Array.isArray(policyItem.data.returnPolicies) 
              ? policyItem.data.returnPolicies 
              : [];
          }
        } else if (policyItem.data?.errors) {
          // Handle error responses
          const errorMessages = policyItem.data.errors.map(err => err.longMessage || err.message).join(", ");
        }
      });
      
      setFulfillmentPolicies(fulfillmentPolicies);
      setPaymentPolicies(paymentPolicies);
      setReturnPolicies(returnPolicies);
    } catch (error) {
      const statusCode = error?.response?.status || error?.status;
      if (statusCode === 403) {
        setIs403Error(true);
        setError("");
      } else {
        setIs403Error(false);
        const businessPolicyMessage = getBusinessPolicyOptInErrorMessage(error);
        setError(
          businessPolicyMessage
            ? getBusinessPolicyOptInErrorNode()
            : error?.response?.data?.message ||
                error?.message ||
                "Failed to load policies"
        );
      }
    } finally {
      setIsLoadingPolicies(false);
    }
  };

  const fetchUserPolicies = async () => {
    try {
      const response = await ApiCollections.getUserPolicies();
      const userPolicies = response?.data || response || {};
      
      console.log("User Policy:", userPolicies);
      
      // Set selected policies if they exist, or clear if null (handle both snake_case and camelCase)
      const fulfillmentId = userPolicies.fulfillment_policy_id || userPolicies.fulfillmentPolicyId;
      const paymentId = userPolicies.payment_policy_id || userPolicies.paymentPolicyId;
      const returnId = userPolicies.return_policy_id || userPolicies.returnPolicyId;
      const vat =
        userPolicies.vat ??
        userPolicies.vat_percentage ??
        userPolicies.vatPercentage ??
        userPolicies.vat_info ??
        userPolicies.vatInfo;
      
      // Convert to string and handle null values
      setSelectedFulfillmentPolicy(fulfillmentId ? String(fulfillmentId) : "");
      setSelectedPaymentPolicy(paymentId ? String(paymentId) : "");
      setSelectedReturnPolicy(returnId ? String(returnId) : "");
      if (vat != null && vat !== "") {
        const sanitized = sanitizeVatInput(String(vat));
        if (sanitized) {
          setVatInfo(sanitized);
          try {
            localStorage.setItem(VAT_INFO_STORAGE_KEY, sanitized);
          } catch {
            /* ignore */
          }
        }
      }
    } catch (error) {
      // Error handling
    }
  };

  const handleFulfillmentPolicyChange = (policyId) => {
    // Toggle: if already selected, deselect it; otherwise select it (only one at a time)
    setSelectedFulfillmentPolicy(selectedFulfillmentPolicy === policyId ? "" : policyId);
  };

  const handlePaymentPolicyChange = (policyId) => {
    // Toggle: if already selected, deselect it; otherwise select it (only one at a time)
    setSelectedPaymentPolicy(selectedPaymentPolicy === policyId ? "" : policyId);
  };

  const handleReturnPolicyChange = (policyId) => {
    // Toggle: if already selected, deselect it; otherwise select it (only one at a time)
    setSelectedReturnPolicy(selectedReturnPolicy === policyId ? "" : policyId);
  };

  const handleCreateFulfillmentPolicy = () => {
    window.open("https://www.ebay.de/bp/ship/create", "_blank");
  };

  const handleCreatePaymentPolicy = () => {
    window.open("https://www.ebay.de/bp/payment/create", "_blank");
  };

  const handleCreateReturnPolicy = () => {
    window.open("https://www.ebay.de/bp/rtn/create", "_blank");
  };

  const handleRefresh = async () => {
    setIs403Error(false);
    await fetchPolicies();
    await fetchUserPolicies();
  };

  const handleConnectToEbay = async () => {
    setIsConnectingToEbay(true);
    try {
      const ensureHttps = (url) => {
        if (url.startsWith("http://") && !url.includes("localhost")) {
          return url.replace("http://", "https://");
        }
        return url;
      };

      const DEFAULT_BASE_URL =
        "https://stickerchecker-new-722491567910.europe-west3.run.app";

      const API_BASE_URL = ensureHttps(
        import.meta.env.VITE_BASE_URL_DEVELOPMENT || DEFAULT_BASE_URL
      );

      const accessToken = localStorage.getItem("access");
      const refreshToken = localStorage.getItem("refresh");

      if (!accessToken && !refreshToken) {
        toastify("error", "No authentication tokens found. Please login again.");
        return;
      }

      let token = accessToken;
      if (!token) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}/api/v1/token/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ refresh: refreshToken }),
          }
        );

        if (!refreshResponse.ok) {
          throw new Error("Failed to refresh token");
        }

        const refreshData = await refreshResponse.json();
        token = refreshData.access;
        localStorage.setItem("access", token);
      }

      const ebaySignUrl = `${API_BASE_URL}/api/v1/ebay/ebaysign/`;

      const requestConfig = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      };

      const response = await fetch(ebaySignUrl, requestConfig);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail ||
            errorData.message ||
            `Request failed: ${response.status}`
        );
      }

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        toastify("error", "Failed to get eBay sign-in URL. Please try again.");
      }
    } catch (error) {
      toastify("error", error?.message || "Failed to connect to eBay. Please try again.");
    } finally {
      setIsConnectingToEbay(false);
    }
  };

  const handleUpdatePolicies = async () => {
    // Build payload with only selected policies
    const payload = {};
    
    if (selectedFulfillmentPolicy) {
      payload.fulfillment_policy_id = selectedFulfillmentPolicy;
    }
    if (selectedPaymentPolicy) {
      payload.payment_policy_id = selectedPaymentPolicy;
    }
    if (selectedReturnPolicy) {
      payload.return_policy_id = selectedReturnPolicy;
    }

    const vatPercentage = parseVatPercentage(vatInfo);
    if (vatInfo.trim() && vatPercentage == null) {
      toastify("warn", "Please enter a VAT percentage between 1 and 100.");
      return;
    }
    if (vatPercentage != null) {
      payload.vat = vatPercentage;
    }

    // At least one policy must be selected
    if (Object.keys(payload).length === 0) {
      toastify("warn", "Please select at least one policy to update.");
      return;
    }

    try {
      setIsUpdatingPolicies(true);
      const response = await ApiCollections.updatePolicies(payload);
      toastify("success", "Policies updated successfully!");
      // Refresh user policies to reflect the update
      await fetchUserPolicies();
      // Refresh eBay status since required_policies may have changed
      dispatch(fetchEbayStatusRequest());
    } catch (error) {
      const businessPolicyMessage = getBusinessPolicyOptInErrorMessage(error);
      toastify(
        "error",
        businessPolicyMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update policies. Please try again."
      );
    } finally {
      setIsUpdatingPolicies(false);
    }
  };

  useEffect(() => {
    try {
      const saved = localStorage.getItem(VAT_INFO_STORAGE_KEY);
      if (saved != null) {
        setVatInfo(sanitizeVatInput(saved));
      }
    } catch {
      /* ignore */
    }
    fetchPolicies();
    fetchUserPolicies();
  }, []);

  const handleVatInfoChange = (e) => {
    const value = sanitizeVatInput(e.target.value);
    setVatInfo(value);
    try {
      localStorage.setItem(VAT_INFO_STORAGE_KEY, value);
    } catch {
      /* ignore */
    }
  };

  if (isLoadingPolicies) {
    return (
      <PoliciesContainer>
        <PoliciesActionsBar>
          <RefreshButton onClick={handleRefresh} disabled={isLoadingPolicies}>
            Refresh
          </RefreshButton>
        </PoliciesActionsBar>
        <LoadingState>Loading policies...</LoadingState>
      </PoliciesContainer>
    );
  }

  if (is403Error) {
    return (
      <PoliciesContainer>
        <PoliciesActionsBar>
          <RefreshButton onClick={handleRefresh} disabled={isLoadingPolicies}>
            Refresh
          </RefreshButton>
        </PoliciesActionsBar>
        <ForbiddenState>
          <ForbiddenTitle>Connection Required</ForbiddenTitle>
          <ForbiddenMessage>
            You need to connect your eBay account to view and manage policies. Please connect to eBay to continue.
          </ForbiddenMessage>
          <ConnectToEbayButton onClick={handleConnectToEbay} disabled={isConnectingToEbay}>
            {isConnectingToEbay ? "Connecting..." : "Connect to eBay"}
          </ConnectToEbayButton>
        </ForbiddenState>
      </PoliciesContainer>
    );
  }

  if (error) {
    return (
      <PoliciesContainer>
        <PoliciesActionsBar>
          <RefreshButton onClick={handleRefresh} disabled={isLoadingPolicies}>
            Refresh
          </RefreshButton>
        </PoliciesActionsBar>
        <ErrorState>{error}</ErrorState>
      </PoliciesContainer>
    );
  }

  return (
    <PoliciesContainer>
      <PoliciesActionsBar>
        <RefreshButton onClick={handleRefresh} disabled={isLoadingPolicies}>
          Refresh
        </RefreshButton>
      </PoliciesActionsBar>

      <PolicySection>
        <PolicySectionHeader>
          <PolicySectionTitle>Fulfillment Policies</PolicySectionTitle>
          <CreatePolicyButton onClick={handleCreateFulfillmentPolicy}>
            Create Fulfillment Policy
          </CreatePolicyButton>
        </PolicySectionHeader>
        <PolicyList>
          {fulfillmentPolicies.length > 0 ? (
            fulfillmentPolicies.map((policy) => {
              const isChecked = String(selectedFulfillmentPolicy) === String(policy.fulfillmentPolicyId);
              const policyKey = `fulfillment-${policy.fulfillmentPolicyId}`;
              const isExpanded = !!expandedPolicies[policyKey];
              return (
              <PolicyItem key={policy.fulfillmentPolicyId}>
                <PolicyCheckbox
                  checked={isChecked}
                  onChange={() => handleFulfillmentPolicyChange(policy.fulfillmentPolicyId)}
                />
                <PolicyContent>
                  <PolicyHeader onClick={() => togglePolicyExpanded(policyKey)}>
                    <PolicyHeaderText>
                      <PolicyName>{policy.name}</PolicyName>
                      <PolicyId>ID: {policy.fulfillmentPolicyId}</PolicyId>
                    </PolicyHeaderText>
                    <PolicyExpandButton
                      type="button"
                      $expanded={isExpanded}
                      aria-label={isExpanded ? "Collapse policy details" : "Expand policy details"}
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePolicyExpanded(policyKey);
                      }}
                    >
                      <ChevronDown size={16} />
                    </PolicyExpandButton>
                  </PolicyHeader>
                  {isExpanded && (
                  <PolicyDetails>
                    <PolicyDetailRow>
                      <PolicyDetailLabel>Marketplace:</PolicyDetailLabel>
                      <PolicyDetailValue>{policy.marketplaceId}</PolicyDetailValue>
                    </PolicyDetailRow>
                    {policy.handlingTime && (
                      <PolicyDetailRow>
                        <PolicyDetailLabel>Handling Time:</PolicyDetailLabel>
                        <PolicyDetailValue>
                          {policy.handlingTime.value} {policy.handlingTime.unit}(s)
                        </PolicyDetailValue>
                      </PolicyDetailRow>
                    )}
                    {policy.shipToLocations && Object.keys(policy.shipToLocations).length > 0 && (
                      <PolicyDetailRow>
                        <PolicyDetailLabel>Ship To Locations:</PolicyDetailLabel>
                        <PolicyDetailValue>
                          {JSON.stringify(policy.shipToLocations)}
                        </PolicyDetailValue>
                      </PolicyDetailRow>
                    )}
                    {policy.shippingOptions && policy.shippingOptions.length > 0 && (
                      <>
                        {policy.shippingOptions.map((option, optionIdx) => (
                          <div key={optionIdx} style={{ marginTop: optionIdx > 0 ? '0.75rem' : '0', paddingTop: optionIdx > 0 ? '0.75rem' : '0', borderTop: optionIdx > 0 ? '1px solid #e5e7eb' : 'none' }}>
                            <PolicyDetailRow>
                              <PolicyDetailLabel>Shipping Option {optionIdx + 1}:</PolicyDetailLabel>
                              <PolicyDetailValue>{option.optionType}</PolicyDetailValue>
                            </PolicyDetailRow>
                            {option.costType && (
                              <PolicyDetailRow>
                                <PolicyDetailLabel>Cost Type:</PolicyDetailLabel>
                                <PolicyDetailValue>{option.costType}</PolicyDetailValue>
                              </PolicyDetailRow>
                            )}
                            {option.shippingDiscountProfileId !== undefined && (
                              <PolicyDetailRow>
                                <PolicyDetailLabel>Shipping Discount Profile ID:</PolicyDetailLabel>
                                <PolicyDetailValue>{option.shippingDiscountProfileId}</PolicyDetailValue>
                              </PolicyDetailRow>
                            )}
                            {option.shippingPromotionOffered !== undefined && (
                              <PolicyDetailRow>
                                <PolicyDetailLabel>Shipping Promotion Offered:</PolicyDetailLabel>
                                <PolicyDetailValue>{option.shippingPromotionOffered ? 'Yes' : 'No'}</PolicyDetailValue>
                              </PolicyDetailRow>
                            )}
                            {option.shippingServices && option.shippingServices.length > 0 && (
                              <>
                                {option.shippingServices.map((service, serviceIdx) => (
                                  <div key={serviceIdx} style={{ marginLeft: '1rem', marginTop: '0.5rem', paddingLeft: '0.75rem', borderLeft: '2px solid #d1d5db' }}>
                                    <PolicyDetailRow>
                                      <PolicyDetailLabel>Service {serviceIdx + 1}:</PolicyDetailLabel>
                                      <PolicyDetailValue></PolicyDetailValue>
                                    </PolicyDetailRow>
                                    {service.sortOrder !== undefined && (
                                      <PolicyDetailRow>
                                        <PolicyDetailLabel>Sort Order:</PolicyDetailLabel>
                                        <PolicyDetailValue>{service.sortOrder}</PolicyDetailValue>
                                      </PolicyDetailRow>
                                    )}
                                    {service.shippingCarrierCode && (
                                      <PolicyDetailRow>
                                        <PolicyDetailLabel>Carrier Code:</PolicyDetailLabel>
                                        <PolicyDetailValue>{service.shippingCarrierCode}</PolicyDetailValue>
                                      </PolicyDetailRow>
                                    )}
                                    {service.shippingServiceCode && (
                                      <PolicyDetailRow>
                                        <PolicyDetailLabel>Service Code:</PolicyDetailLabel>
                                        <PolicyDetailValue>{service.shippingServiceCode}</PolicyDetailValue>
                                      </PolicyDetailRow>
                                    )}
                                    {service.shippingCost && (
                                      <PolicyDetailRow>
                                        <PolicyDetailLabel>Shipping Cost:</PolicyDetailLabel>
                                        <PolicyDetailValue>
                                          {service.shippingCost.value} {service.shippingCost.currency}
                                        </PolicyDetailValue>
                                      </PolicyDetailRow>
                                    )}
                                    {service.additionalShippingCost && (
                                      <PolicyDetailRow>
                                        <PolicyDetailLabel>Additional Shipping Cost:</PolicyDetailLabel>
                                        <PolicyDetailValue>
                                          {service.additionalShippingCost.value} {service.additionalShippingCost.currency}
                                        </PolicyDetailValue>
                                      </PolicyDetailRow>
                                    )}
                                    {service.freeShipping !== undefined && (
                                      <PolicyDetailRow>
                                        <PolicyDetailLabel>Free Shipping:</PolicyDetailLabel>
                                        <PolicyDetailValue>{service.freeShipping ? 'Yes' : 'No'}</PolicyDetailValue>
                                      </PolicyDetailRow>
                                    )}
                                    {service.buyerResponsibleForShipping !== undefined && (
                                      <PolicyDetailRow>
                                        <PolicyDetailLabel>Buyer Responsible For Shipping:</PolicyDetailLabel>
                                        <PolicyDetailValue>{service.buyerResponsibleForShipping ? 'Yes' : 'No'}</PolicyDetailValue>
                                      </PolicyDetailRow>
                                    )}
                                    {service.buyerResponsibleForPickup !== undefined && (
                                      <PolicyDetailRow>
                                        <PolicyDetailLabel>Buyer Responsible For Pickup:</PolicyDetailLabel>
                                        <PolicyDetailValue>{service.buyerResponsibleForPickup ? 'Yes' : 'No'}</PolicyDetailValue>
                                      </PolicyDetailRow>
                                    )}
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                    {policy.categoryTypes && policy.categoryTypes.length > 0 && (
                      <>
                        <PolicyDetailRow>
                          <PolicyDetailLabel>Category Types:</PolicyDetailLabel>
                          <PolicyDetailValue></PolicyDetailValue>
                        </PolicyDetailRow>
                        {policy.categoryTypes.map((cat, idx) => (
                          <PolicyDetailRow key={idx} style={{ marginLeft: '1rem' }}>
                            <PolicyDetailLabel>{cat.name}:</PolicyDetailLabel>
                            <PolicyDetailValue>Default: {cat.default ? 'Yes' : 'No'}</PolicyDetailValue>
                          </PolicyDetailRow>
                        ))}
                      </>
                    )}
                    <PolicyDetailRow>
                      <PolicyDetailLabel>Global Shipping:</PolicyDetailLabel>
                      <PolicyDetailValue>{policy.globalShipping ? 'Yes' : 'No'}</PolicyDetailValue>
                    </PolicyDetailRow>
                    <PolicyDetailRow>
                      <PolicyDetailLabel>Pickup Drop Off:</PolicyDetailLabel>
                      <PolicyDetailValue>{policy.pickupDropOff ? 'Yes' : 'No'}</PolicyDetailValue>
                    </PolicyDetailRow>
                    <PolicyDetailRow>
                      <PolicyDetailLabel>Freight Shipping:</PolicyDetailLabel>
                      <PolicyDetailValue>{policy.freightShipping ? 'Yes' : 'No'}</PolicyDetailValue>
                    </PolicyDetailRow>
                  </PolicyDetails>
                  )}
                </PolicyContent>
              </PolicyItem>
              );
            })
          ) : (
            <LoadingText>No fulfillment policies available</LoadingText>
          )}
        </PolicyList>
      </PolicySection>

      <PolicySection>
        <PolicySectionHeader>
          <PolicySectionTitle>Payment Policies</PolicySectionTitle>
          <CreatePolicyButton onClick={handleCreatePaymentPolicy}>
            Create Payment Policy
          </CreatePolicyButton>
        </PolicySectionHeader>
        <PolicyList>
          {paymentPolicies.length > 0 ? (
            paymentPolicies.map((policy) => {
              const isChecked = String(selectedPaymentPolicy) === String(policy.paymentPolicyId);
              const policyKey = `payment-${policy.paymentPolicyId}`;
              const isExpanded = !!expandedPolicies[policyKey];
              return (
              <PolicyItem key={policy.paymentPolicyId}>
                <PolicyCheckbox
                  checked={isChecked}
                  onChange={() => handlePaymentPolicyChange(policy.paymentPolicyId)}
                />
                <PolicyContent>
                  <PolicyHeader onClick={() => togglePolicyExpanded(policyKey)}>
                    <PolicyHeaderText>
                      <PolicyName>{policy.name}</PolicyName>
                      <PolicyId>ID: {policy.paymentPolicyId}</PolicyId>
                    </PolicyHeaderText>
                    <PolicyExpandButton
                      type="button"
                      $expanded={isExpanded}
                      aria-label={isExpanded ? "Collapse policy details" : "Expand policy details"}
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePolicyExpanded(policyKey);
                      }}
                    >
                      <ChevronDown size={16} />
                    </PolicyExpandButton>
                  </PolicyHeader>
                  {isExpanded && (
                  <PolicyDetails>
                    {policy.marketplaceId && (
                      <PolicyDetailRow>
                        <PolicyDetailLabel>Marketplace:</PolicyDetailLabel>
                        <PolicyDetailValue>{policy.marketplaceId}</PolicyDetailValue>
                      </PolicyDetailRow>
                    )}
                    {policy.immediatePay !== undefined && (
                      <PolicyDetailRow>
                        <PolicyDetailLabel>Immediate Pay:</PolicyDetailLabel>
                        <PolicyDetailValue>{policy.immediatePay ? 'Yes' : 'No'}</PolicyDetailValue>
                      </PolicyDetailRow>
                    )}
                    {policy.paymentMethods && policy.paymentMethods.length > 0 ? (
                      <>
                        <PolicyDetailRow>
                          <PolicyDetailLabel>Payment Methods:</PolicyDetailLabel>
                          <PolicyDetailValue></PolicyDetailValue>
                        </PolicyDetailRow>
                        {policy.paymentMethods.map((method, idx) => (
                          <PolicyDetailRow key={idx} style={{ marginLeft: '1rem' }}>
                            <PolicyDetailLabel>
                              {method.paymentMethodType || `Method ${idx + 1}`}
                            </PolicyDetailLabel>
                            <PolicyDetailValue></PolicyDetailValue>
                          </PolicyDetailRow>
                        ))}
                      </>
                    ) : (
                      <PolicyDetailRow>
                        <PolicyDetailLabel>Payment Methods:</PolicyDetailLabel>
                        <PolicyDetailValue>None</PolicyDetailValue>
                      </PolicyDetailRow>
                    )}
                    {policy.categoryTypes && policy.categoryTypes.length > 0 && (
                      <>
                        <PolicyDetailRow>
                          <PolicyDetailLabel>Category Types:</PolicyDetailLabel>
                          <PolicyDetailValue></PolicyDetailValue>
                        </PolicyDetailRow>
                        {policy.categoryTypes.map((cat, idx) => (
                          <PolicyDetailRow key={idx} style={{ marginLeft: '1rem' }}>
                            <PolicyDetailLabel>{cat.name}:</PolicyDetailLabel>
                            <PolicyDetailValue>Default: {cat.default ? 'Yes' : 'No'}</PolicyDetailValue>
                          </PolicyDetailRow>
                        ))}
                      </>
                    )}
                    {policy.paymentInstructions && (
                      <PolicyDetailRow>
                        <PolicyDetailLabel>Instructions:</PolicyDetailLabel>
                        <PolicyDetailValue>{policy.paymentInstructions}</PolicyDetailValue>
                      </PolicyDetailRow>
                    )}
                  </PolicyDetails>
                  )}
                </PolicyContent>
              </PolicyItem>
              );
            })
          ) : (
            <LoadingText>No payment policies available</LoadingText>
          )}
        </PolicyList>
      </PolicySection>

      <PolicySection>
        <PolicySectionHeader>
          <PolicySectionTitle>Return Policies</PolicySectionTitle>
          <CreatePolicyButton onClick={handleCreateReturnPolicy}>
            Create Return Policy
          </CreatePolicyButton>
        </PolicySectionHeader>
        <PolicyList>
          {returnPolicies.length > 0 ? (
            returnPolicies.map((policy) => {
              const isChecked = String(selectedReturnPolicy) === String(policy.returnPolicyId);
              const policyKey = `return-${policy.returnPolicyId}`;
              const isExpanded = !!expandedPolicies[policyKey];
              return (
              <PolicyItem key={policy.returnPolicyId}>
                <PolicyCheckbox
                  checked={isChecked}
                  onChange={() => handleReturnPolicyChange(policy.returnPolicyId)}
                />
                <PolicyContent>
                  <PolicyHeader onClick={() => togglePolicyExpanded(policyKey)}>
                    <PolicyHeaderText>
                      <PolicyName>{policy.name}</PolicyName>
                      <PolicyId>ID: {policy.returnPolicyId}</PolicyId>
                    </PolicyHeaderText>
                    <PolicyExpandButton
                      type="button"
                      $expanded={isExpanded}
                      aria-label={isExpanded ? "Collapse policy details" : "Expand policy details"}
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePolicyExpanded(policyKey);
                      }}
                    >
                      <ChevronDown size={16} />
                    </PolicyExpandButton>
                  </PolicyHeader>
                  {isExpanded && (
                  <PolicyDetails>
                    {policy.marketplaceId && (
                      <PolicyDetailRow>
                        <PolicyDetailLabel>Marketplace:</PolicyDetailLabel>
                        <PolicyDetailValue>{policy.marketplaceId}</PolicyDetailValue>
                      </PolicyDetailRow>
                    )}
                    {policy.returnsAccepted !== undefined && (
                      <PolicyDetailRow>
                        <PolicyDetailLabel>Returns Accepted:</PolicyDetailLabel>
                        <PolicyDetailValue>{policy.returnsAccepted ? 'Yes' : 'No'}</PolicyDetailValue>
                      </PolicyDetailRow>
                    )}
                    {policy.categoryTypes && policy.categoryTypes.length > 0 && (
                      <>
                        <PolicyDetailRow>
                          <PolicyDetailLabel>Category Types:</PolicyDetailLabel>
                          <PolicyDetailValue></PolicyDetailValue>
                        </PolicyDetailRow>
                        {policy.categoryTypes.map((cat, idx) => (
                          <PolicyDetailRow key={idx} style={{ marginLeft: '1rem' }}>
                            <PolicyDetailLabel>{cat.name}</PolicyDetailLabel>
                            <PolicyDetailValue></PolicyDetailValue>
                          </PolicyDetailRow>
                        ))}
                      </>
                    )}
                    {policy.refundMethod && (
                      <PolicyDetailRow>
                        <PolicyDetailLabel>Refund Method:</PolicyDetailLabel>
                        <PolicyDetailValue>{policy.refundMethod}</PolicyDetailValue>
                      </PolicyDetailRow>
                    )}
                    {policy.returnPeriod && (
                      <PolicyDetailRow>
                        <PolicyDetailLabel>Return Period:</PolicyDetailLabel>
                        <PolicyDetailValue>
                          {policy.returnPeriod.value} {policy.returnPeriod.unit}(s)
                        </PolicyDetailValue>
                      </PolicyDetailRow>
                    )}
                    {policy.returnShippingCostPayer && (
                      <PolicyDetailRow>
                        <PolicyDetailLabel>Shipping Cost Payer:</PolicyDetailLabel>
                        <PolicyDetailValue>{policy.returnShippingCostPayer}</PolicyDetailValue>
                      </PolicyDetailRow>
                    )}
                    {policy.restockingFeePercentage !== undefined && (
                      <PolicyDetailRow>
                        <PolicyDetailLabel>Restocking Fee:</PolicyDetailLabel>
                        <PolicyDetailValue>{policy.restockingFeePercentage}%</PolicyDetailValue>
                      </PolicyDetailRow>
                    )}
                    {policy.description && (
                      <PolicyDetailRow>
                        <PolicyDetailLabel>Description:</PolicyDetailLabel>
                        <PolicyDetailValue>{policy.description}</PolicyDetailValue>
                      </PolicyDetailRow>
                    )}
                    {policy.extendedHolidayReturnsOffered !== undefined && (
                      <PolicyDetailRow>
                        <PolicyDetailLabel>Extended Holiday Returns:</PolicyDetailLabel>
                        <PolicyDetailValue>{policy.extendedHolidayReturnsOffered ? 'Yes' : 'No'}</PolicyDetailValue>
                      </PolicyDetailRow>
                    )}
                  </PolicyDetails>
                  )}
                </PolicyContent>
              </PolicyItem>
              );
            })
          ) : (
            <LoadingText>No return policies available</LoadingText>
          )}
        </PolicyList>
      </PolicySection>

      <PolicySection>
        <PolicySectionTitle>VAT info</PolicySectionTitle>
        <VatCard>
          <VatRow>
            <VatInputWrapper>
              <VatInput
                id="policies-vat-info"
                type="text"
                inputMode="numeric"
                value={vatInfo}
                onChange={handleVatInfoChange}
                placeholder="1–100"
                autoComplete="off"
                aria-label="VAT percentage from 1 to 100"
              />
              <VatPercentSuffix aria-hidden="true">%</VatPercentSuffix>
            </VatInputWrapper>
          </VatRow>
        </VatCard>
      </PolicySection>

      <UpdateButtonContainer>
        <UpdatePolicyButton onClick={handleUpdatePolicies} disabled={isUpdatingPolicies}>
          {isUpdatingPolicies ? "Updating..." : "Update Policies"}
        </UpdatePolicyButton>
      </UpdateButtonContainer>
    </PoliciesContainer>
  );
}

export default Policies;

